import type { PlayerRace } from '@kaeliux/shared';

// Import all background images to ensure they're included in the build
import backgroundBlue from '../assets/images/background-blue.png';
import backgroundGreen from '../assets/images/background-green.png';
import backgroundRed from '../assets/images/background-red.png';
import backgroundNeutral from '../assets/images/background-neutral.png';
import backgroundPortraitBlue from '../assets/images/background-portrait-blue.png';
import backgroundPortraitGreen from '../assets/images/background-portrait-green.png';
import backgroundPortraitRed from '../assets/images/background-portrait-red.png';

/**
 * Gets the appropriate background image based on race and screen orientation
 */
export function getBackgroundImage(race: PlayerRace | undefined, isMobilePortrait: boolean): string {
    // For mobile portrait (9:16 ratio), use portrait backgrounds
    if (isMobilePortrait) {
        switch (race) {
            case 'humans':
                return backgroundPortraitBlue;
            case 'robots':
                return backgroundPortraitGreen;
            case 'axons':
                return backgroundPortraitRed;
            default:
                return backgroundPortraitBlue; // Default to blue for humans
        }
    }

    // Desktop/landscape backgrounds
    switch (race) {
        case 'humans':
            return backgroundBlue;
        case 'robots':
            return backgroundGreen;
        case 'axons':
            return backgroundRed;
        default:
            return backgroundNeutral;
    }
}

/**
 * Checks if the current viewport is mobile portrait (9:16 ratio or taller)
 */
export function isMobilePortraitView(): boolean {
    if (typeof window === 'undefined') return false;

    const width = window.innerWidth;
    const height = window.innerHeight;

    // Mobile portrait: width < height and aspect ratio >= 9:16 (0.5625)
    // Common mobile portrait ratios: 9:16 (0.5625), 9:19.5 (0.461), etc.
    const aspectRatio = width / height;
    return width < height && aspectRatio <= 0.5625 + 0.05; // Small tolerance
}

/**
 * Gets the race-specific CSS color classes
 */
export function getRaceColors(race: PlayerRace | undefined): {
    primary: string;
    secondary: string;
    accent: string;
    glow: string;
} {
    switch (race) {
        case 'humans':
            return {
                primary: 'blue-600',
                secondary: 'blue-400',
                accent: 'blue-300',
                glow: 'blue-500/30',
            };
        case 'robots':
            return {
                primary: 'green-600',
                secondary: 'green-400',
                accent: 'green-300',
                glow: 'green-500/30',
            };
        case 'axons':
            return {
                primary: 'red-600',
                secondary: 'red-400',
                accent: 'red-300',
                glow: 'red-500/30',
            };
        default:
            return {
                primary: 'space-600',
                secondary: 'space-400',
                accent: 'space-300',
                glow: 'space-500/30',
            };
    }
}

/**
 * Gets the race-specific gradient for backgrounds
 */
export function getRaceGradient(race: PlayerRace | undefined): string {
    switch (race) {
        case 'humans':
            return 'from-blue-900/50 via-space-900 to-blue-800/50';
        case 'robots':
            return 'from-green-900/50 via-space-900 to-green-800/50';
        case 'axons':
            return 'from-red-900/50 via-space-900 to-red-800/50';
        default:
            return 'from-space-900 via-space-800 to-space-900';
    }
}
